import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, HelpCircle, MessageCircle, Mail, Phone, FileText, Bug, Star } from 'lucide-react';
import { updatePageMeta } from '../../utils/seo';
import { DOMAIN_CONFIG } from '../../config/domain';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';

const HelpSupportPage: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    updatePageMeta({
      title: 'Help & Support',
      description: 'Get help with πPi Dating. Find answers to common questions, contact support, and learn how to use our Pi Network dating app.',
      keywords: 'help, support, faq, contact, pi dating help',
      url: `https://${DOMAIN_CONFIG.domain}/help`
    });
  }, []);

  const helpCategories = [
    {
      icon: HelpCircle,
      title: 'Frequently Asked Questions',
      description: 'Find answers to common questions',
      action: () => console.log('FAQ')
    },
    {
      icon: MessageCircle,
      title: 'Live Chat Support',
      description: 'Chat with our support team',
      action: () => console.log('Live Chat')
    },
    {
      icon: Mail,
      title: 'Email Support',
      description: 'Send us an email for detailed help',
      action: () => window.open(`mailto:${DOMAIN_CONFIG.email.support}`)
    },
    {
      icon: Bug,
      title: 'Report a Bug',
      description: 'Let us know about technical issues',
      action: () => console.log('Report Bug')
    },
    {
      icon: Star,
      title: 'Feature Request',
      description: 'Suggest new features for the app',
      action: () => console.log('Feature Request')
    }
  ];

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: DOMAIN_CONFIG.email.support,
      action: () => window.open(`mailto:${DOMAIN_CONFIG.email.support}`)
    },
    {
      icon: Phone,
      label: 'Phone',
      value: '+1 (555) 123-4567',
      action: () => window.open('tel:+15551234567')
    }
  ];

  const faqItems = [
    {
      question: 'How do I verify my Pi Network account?',
      answer: 'Connect your Pi wallet through the login process. We automatically verify your Pi Network identity.'
    },
    {
      question: 'How do Pi payments work?',
      answer: 'Premium features are paid using Pi cryptocurrency. Payments are processed securely through the Pi Network.'
    },
    {
      question: 'How do I report inappropriate behavior?',
      answer: 'Use the report button on any profile or message. We take all reports seriously and investigate promptly.'
    },
    {
      question: 'Can I get a refund for my premium subscription?',
      answer: 'Pi payments are generally non-refundable, but we review refund requests on a case-by-case basis.'
    },
    {
      question: 'How do I delete my account?',
      answer: 'Go to Settings > Privacy & Security and scroll down to find the account deletion option.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="p-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/settings')}
          className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-white">Help & Support</h1>
      </div>

      <div className="px-4 pb-8 space-y-6">
        {/* Quick Actions */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-3">How can we help you?</h2>
          <div className="space-y-3">
            {helpCategories.map((category, index) => (
              <Card
                key={index}
                onClick={category.action}
                className="p-4 cursor-pointer hover:shadow-lg transition-all duration-200"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                    <category.icon size={20} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 dark:text-gray-100">{category.title}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{category.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Contact Information */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-3">Contact Us</h2>
          <div className="space-y-3">
            {contactInfo.map((contact, index) => (
              <Card
                key={index}
                onClick={contact.action}
                className="p-4 cursor-pointer hover:shadow-lg transition-all duration-200"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <contact.icon size={20} className="text-blue-600 dark:text-blue-300" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 dark:text-gray-100">{contact.label}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{contact.value}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-3">Frequently Asked Questions</h2>
          <Card className="p-4">
            <div className="space-y-4">
              {faqItems.map((faq, index) => (
                <div key={index} className="border-b border-gray-200 dark:border-gray-600 last:border-b-0 pb-4 last:pb-0">
                  <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">{faq.question}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{faq.answer}</p>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* App Information */}
        <Card className="p-4">
          <div className="text-center">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">πPi Dating</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">Version 1.0.0</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Pioneer Dating App - Connecting hearts through Pi Network
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Visit us at <a href={`https://${DOMAIN_CONFIG.domain}`} className="text-purple-600 dark:text-purple-400 hover:underline">{DOMAIN_CONFIG.domain}</a>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default HelpSupportPage;